import {
  SkyButton,
  SkyMessageBar,
  SkySkeleton,
  SkySkeletonItem,
  SkySwitch,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useLocation, useSearchParams } from "wouter";
import {
  NOTHING_TO_RUN_ISSUE,
  type CreateScheduleBody,
  type ScheduleSummary,
} from "@shared/types/schedules";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { WatchBadge } from "@/components/notifications/WatchBadge";
import { PageCrumb } from "@/components/shell/PageCrumb";
import { ScheduleConnectionsSection } from "@/components/schedules/ScheduleConnectionsSection";
import { ScheduleHistory } from "@/components/schedules/ScheduleHistory";
import { ScheduleNameSection } from "@/components/schedules/ScheduleNameSection";
import { ScheduleSaveBar } from "@/components/schedules/ScheduleSaveBar";
import { ScheduleTimeSection } from "@/components/schedules/ScheduleTimeSection";
import {
  ScheduleDbtJobsSection,
  offersSourceFreshness,
} from "@/components/schedules/ScheduleDbtJobsSection";
import {
  scheduleFieldErrors,
  type ScheduleFieldErrors,
} from "@/components/schedules/scheduleErrors";
import {
  CHOOSE_TIME_REASON,
  INCOMPLETE_INTERVAL_REASON,
  draftFromCron,
  emptyDraft,
  fullRefreshToSave,
  hasChosenTime,
  resolveSchedule,
  scheduleSummary,
  toFriendly,
  type ResolvedSchedule,
  type ScheduleDraft,
  formatInstantNextFire,
} from "@/components/schedules/scheduleForm";
import { describeFailure, reportFailureShown } from "@/lib/feedback/failure";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { useDismissNoticeWhen, useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { usePermissions } from "@/hooks/use-permissions";
import {
  useCreateSchedule,
  useInstanceConnections,
  useSchedule,
  useSchedules,
  useSetScheduleEnabled,
  useUpdateSchedule,
} from "@/hooks/use-schedules";
import { useConnectors } from "@/hooks/use-connectors";
import { clashSentence, findScheduleClashes, type ScheduleClash } from "@/lib/schedules/clashes";
import { COMPLETED_HINT, isCompleted, onceDraftFields } from "@/lib/schedules/once";
import { useTransformationGraph, useDbtJobs } from "@/hooks/use-dbt-jobs";
import { failureMessage, referenceIdToShow } from "@/lib/api";
import { ROUTES, absolutePath } from "@/lib/routes";
import {
  RecordNotFound,
  isMalformedRecordId,
  isNotFoundError,
} from "@/components/access/RecordNotFound";
import { timezoneShortLabel } from "@/lib/timezones";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * The schedule editor (FR-50, UX-1; one page since Session 10, A7).
 *
 * Three cards on one page, in the order a person answers them: Schedule name, What runs
 * (connections, then transformations), When (round 6, S4; it was What runs, When, Then while
 * every schedule imported first). What runs needs at least one connection or transformation (S1):
 * with no connection the models run over what is already in the warehouse. The
 * section rail is gone: behind it, a user saved a schedule without ever seeing its time. The
 * friendly builder is the source of truth and the cron expression is shown back read-only,
 * because a cron string is not something an operator running a clinical import should have to
 * parse to know when their data moves.
 *
 * **No time is ever saved that the user did not pick** (Session 10, B4, SF1). A new schedule
 * starts with no frequency and no time; Save stays disabled with "Choose when this schedule
 * runs." until both are chosen, and then the summary beside Save states the exact next run in
 * the chosen zone. The zone alone is pre-filled (the viewer's display zone when the picker offers
 * it, round 6 W8; else the browser's), and the summary names it.
 *
 * The save path is where the care is. A 422 `DATAFLOW_NOT_READY` is mapped onto the connection
 * list, because the server's detail names the connection and what is missing from it and that
 * sentence belongs beside the list it is about. A 400 maps the same way onto the field that
 * caused it. Whatever cannot be attributed to a field is shown just above Save with the
 * reference id (ux-s10 §3: errors inline where the control remains), and the page scrolls to
 * the first card with an error and focuses it.
 */

type CardKey = "name" | "whatRuns" | "when";

/** Which card a server field belongs to, so a failed save lands the user on it. */
const FIELD_CARD: Record<string, CardKey> = {
  name: "name",
  dataflowIds: "whatRuns",
  refreshMode: "whatRuns",
  fullRefreshDataflowIds: "whatRuns",
  // Round 6, S4: the transformations are part of What runs.
  dbtJobIds: "whatRuns",
  timezone: "when",
  cron: "when",
  friendly: "when",
  skipDates: "when",
};

const CARD_ORDER: readonly CardKey[] = ["name", "whatRuns", "when"];

function firstCardWithError(errors: ScheduleFieldErrors): CardKey | null {
  const cards = new Set(
    Object.keys(errors.fields)
      .map((field) => FIELD_CARD[field.split(".")[0] ?? field])
      .filter((card): card is CardKey => card !== undefined),
  );
  return CARD_ORDER.find((card) => cards.has(card)) ?? null;
}

/** Scrolls an element into view and moves focus to it without a second jump. */
function reveal(element: HTMLElement | null): void {
  if (!element) return;
  // jsdom has no scrollIntoView or matchMedia; a browser always does. A user who asked for
  // reduced motion gets the jump without the animation.
  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  element.scrollIntoView?.({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
  element.focus({ preventScroll: true });
}

/**
 * The editor's two views of a saved schedule (Session 11, P3; owner item 3). Configuration is the
 * editor as it always was; History is what the schedule fired. `?tab=history` opens the second,
 * so a link can land on it; a new schedule has no history and no views to switch, and a reader
 * without `runs.read` gets no switch at all.
 *
 * A pressed-button group rather than `SkyTabList`, as on the connection editor and the run page,
 * for the same reason (ux-s10 §1): in skypoint-ui 2.4.0 the tab triggers pointed `aria-controls`
 * at a panel only the library could render, so axe reported `aria-valid-attr-value` on every
 * screen that rendered its own panel. 2.17.0 fixes that (`skypointUi.libraryContract.test.tsx`);
 * moving to `SkyTabList` + `SkyTabPanel` is a backlog row. Two buttons with `aria-pressed` say
 * the same thing and pass.
 */
type ScheduleView = "configuration" | "history";

const SCHEDULE_VIEW_LABELS: Record<ScheduleView, string> = {
  configuration: "Configuration",
  // Round 4 item 6: one label for a record's runs on both editors.
  history: "Run history",
};

/** The page notice's key for "Schedule saved…", so the next save replaces it. */
const SAVED_NOTICE_KEY = "schedule-saved";

/** The page notice's key for a failed Enabled switch, so a retry replaces it and a success clears it. */
const ENABLED_ERROR_KEY = "schedule-enabled-error";

/**
 * The form-level part of a failed save as the inline bar shows it: the sentence the server could
 * not attribute to a field, "Reference id {id}." once, and Copy reference. Each failed save is a
 * new occurrence, so the bar's alert is announced again for a repeat of the same failure.
 */
function saveFailure(
  errors: ScheduleFieldErrors | null,
  occurrence: number,
  retryable: boolean,
): InlineFailure | null {
  if (!errors?.form) return null;
  return {
    message: errors.form,
    reference: errors.reference,
    printedReference: referenceIdToShow(errors.form, errors.reference),
    retryable,
    occurrence,
  };
}

/** The success sentence (ux-s10 §3 copy): "Schedule saved. Next import Tue Sep 30, 2:00 AM EDT" (in the display zone, #1079) */
function savedMessage(saved: ScheduleSummary, displayZone: string): string {
  if (!saved.enabled) return "Schedule saved. Not scheduled while disabled.";
  if (isCompleted(saved)) return `Schedule saved. ${COMPLETED_HINT}.`;
  // Enabled with no next run: its skip dates leave no firing inside two years (P9).
  if (!saved.nextFireAt) return "Schedule saved. No upcoming run.";
  return `Schedule saved. Next import ${formatInstantNextFire(new Date(saved.nextFireAt), displayZone)}.`;
}

/**
 * The form as a stored row reads: on load, and again from the row a save answers with
 * (2026-09-28, owner: "it stays on the same screen and does not even refresh"), as the connection
 * editor re-seeds from its saved row. What the form shows after a save is what is stored.
 */
function draftFromRow(loaded: ScheduleSummary): ScheduleDraft {
  // Round 10 wave 2 (S3): a Once schedule reopens on its dates, in its zone.
  const fromCron = onceDraftFields(loaded) ?? draftFromCron(loaded.cron);
  return {
    ...emptyDraft(),
    ...(fromCron ?? {}),
    // An expression the builder cannot represent is kept as it is (SF2), never replaced by
    // an empty builder: renaming an API-created `*/5 * * * *` schedule must not re-time it.
    customCron: fromCron ? null : loaded.cron,
    // Round 5: held as loaded, so a form that only passes through the time card is not dirty.
    storedCron: loaded.cron,
    name: loaded.name,
    timezone: loaded.timezone,
    enabled: loaded.enabled,
    dataflowIds: loaded.dataflowIds,
    dbtJobIds: loaded.dbtJobIds,
    // Session 10 (P9). An older API answer carries neither; the defaults are what the row
    // means then. The pause is read from the row on every render (`draft` in the page).
    skipDates: loaded.skipDates ?? [],
    // Round 4, item 7. An older answer carries no set: its full mode meant every connection.
    fullRefreshDataflowIds:
      loaded.fullRefreshDataflowIds ??
      (loaded.refreshMode === "full" ? [...loaded.dataflowIds] : []),
    // Round 7. An older answer carries none: the check was off.
    checkSourceFreshness: loaded.checkSourceFreshness ?? false,
  };
}

function EditorSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      {[0, 1, 2].map((card) => (
        <div key={card} className="rounded-xl border border-border bg-card p-5">
          <SkySkeleton>
            <SkySkeletonItem size={20} width={220} />
            <div className="h-4" />
            <SkySkeletonItem size={32} />
            <div className="h-4" />
            <SkySkeletonItem size={64} />
          </SkySkeleton>
        </div>
      ))}
    </div>
  );
}

/** The views are a saved schedule's, for a reader of its runs; then `?tab=history` picks History. */
function scheduleViewOf(hasViews: boolean, tab: string | null): ScheduleView {
  if (!hasViews) return "configuration";
  return tab === "history" ? "history" : "configuration";
}

/** Beside Save: when it runs and the next run, or, before a time is chosen, the zone. */
function saveBarSummary(
  resolved: ResolvedSchedule,
  draft: ScheduleDraft,
  displayZone: string,
): string | undefined {
  if (resolved.ok) return scheduleSummary(resolved, draft.timezone, draft.enabled, displayZone);
  return draft.timezone ? `Time zone ${timezoneShortLabel(draft.timezone)}` : undefined;
}

/** The saved schedule is enabled and has no next run (Session 10, P9). */
function hasNoUpcomingRun(row: ScheduleSummary | undefined): boolean {
  return Boolean(row && row.enabled && row.nextFireAt === null);
}

function lastSavedLabel(row: ScheduleSummary | undefined, zone: string): string | undefined {
  return row
    ? `Last saved ${formatInstant(row.updatedAt, zone, "short", { named: true })}`
    : undefined;
}

/** The header's Enabled switch for a writer, the same word as plain text for a reader. */
function EnabledSwitch({
  canWrite,
  isNew,
  shown,
  enabled,
  saving,
  onChange,
}: {
  canWrite: boolean;
  isNew: boolean;
  shown: boolean;
  enabled: boolean;
  saving: boolean;
  onChange: (next: boolean) => void;
}) {
  if (!canWrite) {
    if (isNew) return null;
    return (
      <span data-testid="schedule-enabled-state">
        <SkyText size={200}>{enabled ? "Enabled" : "Disabled"}</SkyText>
      </span>
    );
  }
  if (!shown) return null;
  return (
    <SkySwitch
      label={enabled ? "Enabled" : "Disabled"}
      checked={enabled}
      onCheckedChange={onChange}
      loading={saving}
      testId="schedule-enabled"
    />
  );
}

/** The caller's own Watch of a saved schedule, once it is read. */
function ScheduleWatch({ schedule }: { schedule: ScheduleSummary | undefined }) {
  if (!schedule) return null;
  return <WatchBadge targetType="schedule" targetId={schedule.id} name={schedule.name} />;
}

/**
 * What the page shows instead of the editor: no access to create one, the saved schedule still
 * loading, or its read failed. Null when the editor shows.
 */
function editorGate({
  canWrite,
  isNew,
  schedule,
  idMalformed,
}: {
  canWrite: boolean;
  isNew: boolean;
  schedule: ReturnType<typeof useSchedule>;
  idMalformed: boolean;
}): ReactElement | null {
  if (idMalformed || (!isNew && schedule.isError && isNotFoundError(schedule.error))) {
    return (
      <RecordNotFound
        noun="schedule"
        listLabel="Back to Schedules"
        listPath={ROUTES.schedules}
        testId="schedule-not-found"
      />
    );
  }
  if (!canWrite && isNew) {
    return (
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        title="You do not have access to this screen"
        message="This screen needs the schedules.write permission in this instance. Ask an administrator to grant it."
        testId="permission-denied"
      />
    );
  }

  if (!isNew && schedule.isLoading) return <EditorSkeleton />;

  if (!isNew && schedule.isError) {
    return (
      <SkyMessageBar
        intent="error"
        layout="multiline"
        title="We could not load this schedule"
        message={failureMessage(schedule.error, "Try again.")}
        testId="schedule-load-error"
      />
    );
  }

  return null;
}

/** Configuration and History, for a saved schedule a reader of runs opens. */
function ScheduleViewSwitch({
  view,
  onSelect,
}: {
  view: ScheduleView;
  onSelect: (view: ScheduleView) => void;
}) {
  return (
    <fieldset
      aria-label="What to show for this schedule"
      className="min-w-0 mb-4 flex max-w-4xl items-center gap-2 border-b border-border pb-2"
      data-testid="schedule-views"
    >
      {(["configuration", "history"] as const).map((value) => (
        <SkyButton
          key={value}
          appearance={view === value ? "secondary" : "subtle"}
          size="small"
          aria-pressed={view === value}
          onClick={() => onSelect(value)}
          testId={`schedule-view-${value}`}
        >
          {SCHEDULE_VIEW_LABELS[value]}
        </SkyButton>
      ))}
    </fieldset>
  );
}

function ReadOnlyNote() {
  return (
    <div className="mb-4">
      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="This schedule is read-only for you"
        message="Changing a schedule needs the schedules.write permission in this instance. Everything below is shown exactly as it is stored."
        testId="schedule-read-only"
      />
    </div>
  );
}

/** The page's title: a saved schedule's name, else "New schedule". */
function editorTitle(scheduleId: string | undefined, row: ScheduleSummary | undefined): string {
  return scheduleId ? (row?.name ?? "Schedule") : "New schedule";
}

/** The History view of a saved schedule. */
function HistoryPanel({
  shown,
  schedule,
}: {
  shown: boolean;
  schedule: ScheduleSummary | undefined;
}) {
  if (!shown || !schedule) return null;
  return (
    // Not a second landmark: the History is its own section, titled "History".
    <div className="max-w-6xl" data-testid="schedule-tabpanel-history">
      <ScheduleHistory scheduleId={schedule.id} scheduleName={schedule.name} />
    </div>
  );
}

/** What the clash check reads from the resolved draft: its cron, or a Once draft's dates. */
function clashInputsOf(resolved: ResolvedSchedule) {
  return {
    clashCron: resolved.ok ? resolved.cron : null,
    clashDates: resolved.ok ? resolved.runDates : undefined,
  };
}

/** #1652: an id that is not a uuid is never asked for; the page reads not found. */
function scheduleIdToRead(scheduleId: string | undefined): {
  idMalformed: boolean;
  idToRead: string | null;
} {
  const idMalformed = scheduleId !== undefined && isMalformedRecordId(scheduleId);
  return { idMalformed, idToRead: idMalformed ? null : (scheduleId ?? null) };
}

/** W11: the transformations list has loaded and holds no model. */
function hasNoModels(models: unknown): boolean {
  return Array.isArray(models) && models.length === 0;
}

/** Why Save is off, or undefined when the draft can be saved. */
function blockedReasonOf(draft: ScheduleDraft, resolved: ResolvedSchedule): string | undefined {
  if (!draft.name.trim()) return "A schedule needs a name before it can be saved.";
  // Round 6, S1: connections, transformations or both; never neither.
  if (draft.dataflowIds.length === 0 && draft.dbtJobIds.length === 0) {
    return NOTHING_TO_RUN_ISSUE;
  }
  // Before the resolved check: a draft with no frequency or no time has nothing to resolve,
  // and the reason names the decision still to make (Session 10, B4).
  // An interval with its step or window still to choose says which (2026-09-28).
  if (!draft.timezone || (!hasChosenTime(draft) && !draft.customCron)) {
    return draft.frequency === "Interval" ? INCOMPLETE_INTERVAL_REASON : CHOOSE_TIME_REASON;
  }
  // A time row with no time, or the same time twice, is named here and on its row.
  if (!resolved.ok) return resolved.issue;
  return undefined;
}

/** The body a save sends, from a draft that resolved. */
function scheduleBodyOf(
  draft: ScheduleDraft,
  resolved: Extract<ResolvedSchedule, { ok: true }>,
  connections: Parameters<typeof fullRefreshToSave>[1],
  connectors: Parameters<typeof fullRefreshToSave>[2],
): CreateScheduleBody {
  // `cronSchedules` is validated by the shared schema on the server; sending the friendly
  // builder rather than the expression means the server does the collapse with the same
  // function this screen previewed it with. A kept custom expression goes back as `cron`,
  // exactly as it is stored (SF2).
  // Round 10 wave 2 (S3): Once sends its dates as `friendly` (`Dates`), with no expression.
  const friendly = resolved.source === "custom" ? null : toFriendly(draft);
  const when: Pick<CreateScheduleBody, "cron" | "friendly"> = friendly
    ? { friendly }
    : { cron: resolved.cron ?? undefined };
  return {
    name: draft.name.trim(),
    ...when,
    timezone: draft.timezone,
    enabled: draft.enabled,
    dataflowIds: draft.dataflowIds,
    dbtJobIds: draft.dbtJobIds,
    skipDates: draft.skipDates,
    // Round 4, item 7: the set decides; the server derives `refreshMode` from it.
    fullRefreshDataflowIds: fullRefreshToSave(draft, connections, connectors),
    // Round 7: offered, and so sent on, only for a schedule of dbt jobs and no connection.
    checkSourceFreshness: offersSourceFreshness(draft) && draft.checkSourceFreshness,
  };
}

/** The bars under the cards: a Once schedule that has run all its dates, and same-time clashes. */
function ScheduleEditorNotices({
  schedule,
  clashes,
  connectionNames,
}: {
  schedule: ScheduleSummary | undefined;
  clashes: readonly ScheduleClash[];
  connectionNames: ReadonlyMap<string, string>;
}) {
  return (
    <>
      {/* Round 10 wave 2 (S5): a Once schedule that has run all of its dates. */}
      {isCompleted(schedule) ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          title={COMPLETED_HINT}
          message="Every date of this schedule has run. Add a date or choose another frequency to run it again."
          testId="schedule-completed"
        />
      ) : null}

      {clashes.length > 0 ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="Another schedule runs at the same time"
          message={clashes.map((clash) => clashSentence(clash, connectionNames)).join(" ")}
          testId="schedule-clash-warning"
        />
      ) : null}
    </>
  );
}

export default function ScheduleEditorPage({ scheduleId }: { scheduleId?: string } = {}) {
  const zone = useDisplayTz();
  const [, navigate] = useLocation();
  const [searchParams] = useSearchParams();
  const notice = useNotice();
  // The Enabled switch's failure, said above Save with the save's own (contract §14).
  const showApiError = useApiErrorNotice();
  const { canAny } = usePermissions();

  const isNew = !scheduleId;
  const canWrite = canAny("schedules.write");
  const canReadRuns = canAny("runs.read");
  const hasViews = !isNew && canReadRuns;
  const view = scheduleViewOf(hasViews, searchParams.get("tab"));
  // Replace, not a new history entry: a view is not a page the back button should step through.
  // The form lives on this page, so an unsaved edit survives a look at the History.
  const selectView = (next: ScheduleView) => {
    if (!scheduleId || next === view) return;
    navigate(absolutePath(`/schedules/${scheduleId}${next === "history" ? "?tab=history" : ""}`), {
      replace: true,
    });
  };

  // #1652: an id that is not a uuid is never asked for; the page reads not found.
  const { idMalformed, idToRead } = scheduleIdToRead(scheduleId);
  const schedule = useSchedule(idToRead);
  const connections = useInstanceConnections();
  // Round 9 (B-28): the other schedules, to warn before two enabled ones share a firing minute.
  const otherSchedules = useSchedules(canWrite);
  // S2 and W26: which connectors import incrementally, and their display names.
  const connectors = useConnectors();
  const transformations = useDbtJobs();
  // W11: with no model yet, whether a dbt repository is bound decides what the empty state says.
  const noModels = hasNoModels(transformations.data);
  const graph = useTransformationGraph(noModels);

  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule(scheduleId ?? "");
  const setEnabled = useSetScheduleEnabled(scheduleId ?? "");

  // W8: a new schedule starts in the viewer's display zone when the picker offers it.
  const [formDraft, setDraft] = useState<ScheduleDraft>(() => emptyDraft(zone));
  // A display zone that arrives after the first render (`/me` still loading) still moves a new
  // schedule's zone, as long as it is the default the editor chose and not one the user picked.
  const defaultZone = useRef(formDraft.timezone);
  useEffect(() => {
    if (!isNew) return;
    const next = emptyDraft(zone).timezone;
    if (next === defaultZone.current) return;
    const previous = defaultZone.current;
    defaultZone.current = next;
    setDraft((current) =>
      current.timezone === previous ? { ...current, timezone: next } : current,
    );
  }, [isNew, zone]);
  // The pause is the saved row's, never the form's: Pause and Resume are on the Schedules list and
  // a save does not change it, so every render reads it from the row (P9). The next run beside
  // Save is then the one the runner will keep.
  const storedPause = schedule.data?.pausedUntil ?? null;
  // Round 5: the saved expression, read the same way. A schedule stored off the quarter hour saves
  // unchanged while the form still resolves to it (`resolveSchedule`).
  const storedCron = schedule.data?.cron ?? null;
  const draft = useMemo<ScheduleDraft>(
    () => ({ ...formDraft, pausedUntil: storedPause, storedCron }),
    [formDraft, storedPause, storedCron],
  );
  // The form as last loaded or saved. "Schedule saved" is never shown over unsaved edits
  // (ux-s10 §3; review r1 SF4): it clears once the form differs from this.
  const [baseline, setBaseline] = useState<ScheduleDraft | null>(null);
  const isDirty = useMemo(
    () => baseline !== null && JSON.stringify(formDraft) !== JSON.stringify(baseline),
    [formDraft, baseline],
  );
  useDismissNoticeWhen(SAVED_NOTICE_KEY, isDirty);
  const [errors, setErrors] = useState<ScheduleFieldErrors | null>(null);
  /** The server said the failed save may succeed if tried again. */
  const [saveRetryable, setSaveRetryable] = useState(false);
  /** Bumped by every failed save, so the same error twice still moves the user to it. */
  const [failedSaves, setFailedSaves] = useState(0);
  /** The id this draft was seeded from, so a refetch never overwrites an edit. */
  const seededFrom = useRef<string | null>(null);

  const nameRef = useRef<HTMLElement>(null);
  const whatRunsRef = useRef<HTMLElement>(null);
  const whenRef = useRef<HTMLElement>(null);
  const formErrorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loaded = schedule.data;
    if (!loaded || seededFrom.current === loaded.id) return;
    seededFrom.current = loaded.id;
    const seeded = draftFromRow(loaded);
    setDraft(seeded);
    setBaseline(seeded);
  }, [schedule.data]);

  // A failed save moves the user to what failed: the first card carrying a field error, or the
  // message above Save when nothing names a field.
  useEffect(() => {
    if (failedSaves === 0 || !errors) return;
    const cards: Record<CardKey, HTMLElement | null> = {
      name: nameRef.current,
      whatRuns: whatRunsRef.current,
      when: whenRef.current,
    };
    const card = firstCardWithError(errors);
    reveal(card ? cards[card] : formErrorRef.current);
  }, [failedSaves, errors]);

  const resolved = useMemo(() => resolveSchedule(draft), [draft]);

  // B-28: a clash is what the draft would do once saved, so it follows the draft's cron, zone,
  // connections and Enabled switch, never the saved row's.
  // Round 10 wave 2 (S3): a Once draft's instants, tested against the others' firings.
  const { clashCron, clashDates } = clashInputsOf(resolved);
  const clashes = useMemo(
    () =>
      findScheduleClashes(
        {
          id: scheduleId ?? null,
          cron: clashCron,
          timezone: draft.timezone,
          enabled: draft.enabled,
          dataflowIds: draft.dataflowIds,
          ...(clashDates ? { runDates: clashDates } : {}),
        },
        otherSchedules.data ?? [],
      ),
    [
      scheduleId,
      clashCron,
      clashDates,
      draft.timezone,
      draft.enabled,
      draft.dataflowIds,
      otherSchedules.data,
    ],
  );
  const connectionNames = useMemo(
    () => new Map((connections.data ?? []).map((row) => [row.id, row.displayName])),
    [connections.data],
  );

  const blockedReason = useMemo(() => blockedReasonOf(draft, resolved), [draft, resolved]);

  /**
   * Save was pressed while blocked, so an empty time row says so on the row (review r3 nit).
   * Cleared once nothing blocks Save, so a row added after that waits again (state adjusted
   * during render, as React's "storing information from previous renders" describes).
   */
  const [blockedSaveAttempted, setBlockedSaveAttempted] = useState(false);
  if (blockedSaveAttempted && !blockedReason) setBlockedSaveAttempted(false);
  /**
   * S4: a blocked Save that runs nothing says why at the top of What runs and moves focus there.
   * Bumped per attempt so a second press moves focus again. The move waits a task: the press is a
   * `pointerdown` on the disabled button's wrapper, and the browser's own focus handling of the
   * `mousedown` that follows would otherwise take focus straight back off the card.
   */
  const [nothingToRunAttempts, setNothingToRunAttempts] = useState(0);
  const nothingToRun = blockedSaveAttempted && blockedReason === NOTHING_TO_RUN_ISSUE;
  useEffect(() => {
    if (nothingToRunAttempts === 0) return;
    const timer = window.setTimeout(() => reveal(whatRunsRef.current), 0);
    return () => window.clearTimeout(timer);
  }, [nothingToRunAttempts]);

  /** Beside Save: when it runs and the next run, or, before a time is chosen, the zone. */
  const summary = saveBarSummary(resolved, draft, zone);

  const save = async (): Promise<void> => {
    if (blockedReason || !resolved.ok) return;
    setErrors(null);
    // What this save writes; an edit made while it is in flight stays unsaved (dirty).
    const submitted = formDraft;
    const body = scheduleBodyOf(draft, resolved, connections.data ?? [], connectors.data ?? []);

    notice.dismiss(ENABLED_ERROR_KEY);
    try {
      if (isNew) {
        const created = await createSchedule.mutateAsync(body);
        const page = `/schedules/${created.id}`;
        // Posted for the new schedule's page, so it survives the navigation there and is read
        // under that page's title (ux-s10 §3).
        notice.success(savedMessage(created, zone), { key: SAVED_NOTICE_KEY, forPath: page });
        navigate(absolutePath(page));
        return;
      }
      const saved = await updateSchedule.mutateAsync(body);
      // The form becomes the row the server stored (its order, its pruned skip dates, its
      // name), unless the user edited while the save was in flight: that edit stays, unsaved.
      const stored = draftFromRow(saved);
      setDraft((current) => (current === submitted ? stored : current));
      setBaseline(stored);
      notice.success(savedMessage(saved, zone), { key: SAVED_NOTICE_KEY });
    } catch (error) {
      // Inline, where the control remains (ux-s10 §3): the field messages on their cards and
      // the rest, with the reference id, just above Save. Logged with identifiers only.
      reportFailureShown(error);
      setSaveRetryable(describeFailure(error).retryable);
      setErrors(scheduleFieldErrors(error));
      setFailedSaves((count) => count + 1);
    }
  };

  const toggleEnabled = async (next: boolean): Promise<void> => {
    if (isNew) {
      setDraft({ ...draft, enabled: next });
      return;
    }
    notice.dismiss(ENABLED_ERROR_KEY);
    try {
      const saved = await setEnabled.mutateAsync(next);
      setDraft((current) => ({ ...current, enabled: saved.enabled }));
      // The switch saves at once, so the saved form moves with it and is not an unsaved edit.
      setBaseline((current) => (current ? { ...current, enabled: saved.enabled } : current));
      notice.success(saved.enabled ? `${saved.name} is enabled.` : `${saved.name} is disabled.`);
    } catch (error) {
      // The switch sits in the page header, so its failure is the page notice right under it and
      // focus stays on the switch (ux-s10 §3), rather than a bar above Save that takes focus away.
      showApiError(error, "The schedule could not be changed.", {
        key: ENABLED_ERROR_KEY,
        title: "We could not change this schedule",
      });
    }
  };

  // Called, not rendered as a component: the page returns the same element it always did.
  const gate = editorGate({ canWrite, isNew, schedule, idMalformed });
  if (gate) return gate;

  const isSaving = createSchedule.isPending || updateSchedule.isPending;
  const title = editorTitle(scheduleId, schedule.data);
  const fieldErrors = errors?.fields ?? {};

  return (
    <>
      <TopbarActionsPortal>
        <EnabledSwitch
          canWrite={canWrite}
          isNew={isNew}
          shown={canWrite}
          enabled={draft.enabled}
          saving={setEnabled.isPending}
          onChange={(next) => void toggleEnabled(next)}
        />
        {/* Alert routing (owner 2026-09-28): the caller's own Watch of this schedule, for every
            role that reads schedules. */}
        {isNew ? null : <ScheduleWatch schedule={schedule.data} />}
      </TopbarActionsPortal>

      {/* The trail is the shell's breadcrumb row (post-release 607e63a); the page names it. */}
      <PageCrumb label={title} />

      {hasViews ? <ScheduleViewSwitch view={view} onSelect={selectView} /> : null}

      <HistoryPanel shown={view === "history"} schedule={schedule.data} />

      {/* Hidden, not unmounted, while the History is open: the form keeps its edits and its
          scroll, and nothing is fetched again on the way back. The save bar and its error bar
          belong to Configuration only. */}
      <div hidden={view === "history"} data-testid="schedule-tabpanel-configuration">
        {canWrite ? null : <ReadOnlyNote />}

        <div className="flex max-w-4xl flex-col gap-5" data-testid="schedule-editor-cards">
          <ScheduleNameSection
            ref={nameRef}
            draft={draft}
            onChange={setDraft}
            fieldErrors={fieldErrors}
            disabled={!canWrite}
          />

          <ScheduleConnectionsSection
            ref={whatRunsRef}
            draft={draft}
            onChange={setDraft}
            connections={connections.data ?? []}
            connectors={connectors.data}
            isLoading={connections.isLoading}
            fieldErrors={fieldErrors}
            disabled={!canWrite}
            nothingToRun={nothingToRun ? NOTHING_TO_RUN_ISSUE : undefined}
          >
            <ScheduleDbtJobsSection
              draft={draft}
              onChange={setDraft}
              transformations={transformations.data}
              isLoading={transformations.isLoading}
              repoBound={graph.data?.bundle.repoBound}
              fieldErrors={fieldErrors}
              disabled={!canWrite}
            />
          </ScheduleConnectionsSection>

          <ScheduleTimeSection
            ref={whenRef}
            draft={draft}
            onChange={setDraft}
            // The row's own zone, not the draft's: a schedule stored in a zone this product no
            // longer offers keeps it on offer and says so, however the draft is edited.
            storedTimezone={schedule.data?.timezone ?? null}
            fieldErrors={fieldErrors}
            disabled={!canWrite}
            storedNoUpcomingRun={hasNoUpcomingRun(schedule.data) && !isCompleted(schedule.data)}
            revealIncompleteRows={blockedSaveAttempted}
          />

          <ScheduleEditorNotices
            schedule={schedule.data}
            clashes={clashes}
            connectionNames={connectionNames}
          />
        </div>

        {/* Errors inline, directly above Save (ux-s10 §3). Always mounted so it can be focused
          and scrolled to; empty when there is nothing to say. */}
        <div
          ref={formErrorRef}
          tabIndex={-1}
          className="mt-5 flex max-w-4xl flex-col gap-3 outline-none"
        >
          <InlineErrorBar
            error={saveFailure(errors, failedSaves, saveRetryable)}
            // A retryable failure reads "Try again" (the bar's default title).
            title={saveRetryable ? undefined : "We could not save this schedule"}
            testId="schedule-save-error"
          />
        </div>
      </div>

      {canWrite && view === "configuration" ? (
        <ScheduleSaveBar
          onSave={() => void save()}
          onCancel={() => navigate(absolutePath("/schedules"))}
          isSaving={isSaving}
          blockedReason={blockedReason}
          onBlockedAttempt={() => {
            setBlockedSaveAttempted(true);
            if (blockedReason === NOTHING_TO_RUN_ISSUE) setNothingToRunAttempts((n) => n + 1);
          }}
          // What Save will write, in words, beside the button: when, in which zone, and the
          // exact next run there. Before a time is chosen, the zone it will be in.
          summary={summary}
          saveLabel={isNew ? "Create schedule" : "Save schedule"}
          lastSavedLabel={lastSavedLabel(schedule.data, zone)}
        />
      ) : null}
    </>
  );
}
